// The unit skeleton: dimensions, each with ONE canonical storage unit and a
// menu of display units. Stored values are always canonical (a height is
// always meters in the document); display units exist only at the edge, so
// nothing downstream — grids, documents, exports — ever converts.
//
// Deliberately minimal: dimensions join when a feature needs them. The Ruby
// side gains its mirror when a server-side renderer needs units (the Master
// Contract, phase 2).

// Compound presentation of a unit: the unit renders and edits as a
// major/minor pair ("5 ft 7 in") instead of a decimal ("5.58 ft"). This is a
// PRESENTATION property of the unit, not a separate unit — the menu choice
// stays "ft", the stored preference stays a two-value enum, and the value
// under the pair is still one number in the display unit.
interface CompoundPresentation {
  minorLabel: string
  minorPerMajor: number
  // Display rounding of the minor part ("7 in", never "7.03 in").
  minorDecimals: number
}

interface DisplayUnit {
  label: string
  // Conversion to/from the dimension's canonical unit.
  toCanonical: (value: number) => number
  fromCanonical: (value: number) => number
  // Display rounding only — stored values keep full precision.
  decimals: number
  compound?: CompoundPresentation
}

interface Dimension {
  canonical: string
  // The unit a generic reading context formats in when nothing chooses one —
  // per-document preferences (height_unit, weight_unit) override where they
  // exist.
  defaultDisplay: string
  units: Record<string, DisplayUnit>
}

const METERS_PER_FOOT = 0.3048
const MINUTES_PER_HOUR = 60
const KILOGRAMS_PER_POUND = 0.45359237

export const Dimensions: Record<string, Dimension> = {
  length: {
    canonical: "m",
    defaultDisplay: "m",
    units: {
      m: {
        label: "m",
        toCanonical: (v) => v,
        fromCanonical: (v) => v,
        decimals: 2,
      },
      ft: {
        label: "ft",
        toCanonical: (v) => v * METERS_PER_FOOT,
        fromCanonical: (v) => v / METERS_PER_FOOT,
        decimals: 2,
        // Nobody wants decimal feet — heights read as feet-and-inches.
        compound: { minorLabel: "in", minorPerMajor: 12, minorDecimals: 0 },
      },
    },
  },
  // Weights are stored in kilograms. Decimal pounds are idiomatic ("110.2
  // lb"), so no compound presentation; stones are YAGNI.
  mass: {
    canonical: "kg",
    defaultDisplay: "kg",
    units: {
      kg: {
        label: "kg",
        toCanonical: (v) => v,
        fromCanonical: (v) => v,
        decimals: 1,
      },
      lb: {
        label: "lb",
        toCanonical: (v) => v * KILOGRAMS_PER_POUND,
        fromCanonical: (v) => v / KILOGRAMS_PER_POUND,
        decimals: 1,
      },
    },
  },
  // Durations are stored as integer minutes. There is no stored unit
  // preference: min is a legitimate plain presentation ("90 min"), so each
  // call site picks — hr for reading contexts (compound, "1 hr 30 min",
  // decimal hours being the thing nobody wants), min where terseness wins.
  duration: {
    canonical: "min",
    defaultDisplay: "hr",
    units: {
      min: {
        label: "min",
        toCanonical: (v) => v,
        fromCanonical: (v) => v,
        decimals: 0,
      },
      hr: {
        label: "hr",
        toCanonical: (v) => v * MINUTES_PER_HOUR,
        fromCanonical: (v) => v / MINUTES_PER_HOUR,
        decimals: 2,
        compound: { minorLabel: "min", minorPerMajor: MINUTES_PER_HOUR, minorDecimals: 0 },
      },
    },
  },
}

const round = (value: number, decimals: number): number => {
  const factor = 10 ** decimals
  return Math.round(value * factor) / factor
}

export const convert = (dimension: string, value: number, from: string, to: string): number => {
  const dim = Dimensions[dimension]
  return dim.units[to].fromCanonical(dim.units[from].toCanonical(value))
}

// A display-precision conversion for swapping the unit of an input in place.
// Slightly wider than display rounding so a round-trip drifts less than the
// displayed precision (4 ft -> 1.2192 m -> 4 ft exactly).
export const convertForInput = (
  dimension: string,
  value: number,
  from: string,
  to: string
): number => round(convert(dimension, value, from, to), 4)

// A display-unit value split into its compound pair, with the minor part
// rounded for display and the carry handled (5 ft 11.99 in -> 6 ft 0 in).
export const splitCompound = (
  dimension: string,
  displayValue: number,
  unit: string
): { major: number; minor: number } => {
  const { compound } = Dimensions[dimension].units[unit]

  let major = Math.trunc(displayValue)
  let minor = round((displayValue - major) * compound.minorPerMajor, compound.minorDecimals)
  if (minor >= compound.minorPerMajor) {
    major += 1
    minor = 0
  }
  return { major, minor }
}

// The inverse: a compound pair back to one display-unit value.
export const joinCompound = (
  dimension: string,
  { major, minor }: { major: number; minor: number },
  unit: string
): number => {
  const { compound } = Dimensions[dimension].units[unit]
  return major + minor / compound.minorPerMajor
}

// "1.22 m", or "5 ft 7 in" for a compound-presented unit ("5 ft" when the
// minor part is zero, "7 in" when the major is).
export const formatIn = (dimension: string, canonicalValue: number, unit: string): string => {
  const display = Dimensions[dimension].units[unit]
  const value = display.fromCanonical(canonicalValue)

  if (display.compound) {
    const { major, minor } = splitCompound(dimension, value, unit)
    // A bare minor part (zero included) reads in the minor unit: "7 in",
    // "45 min", "0 min" — never "0 hr".
    if (major === 0) return `${minor} ${display.compound.minorLabel}`
    if (minor === 0) return `${major} ${display.label}`
    return `${major} ${display.label} ${minor} ${display.compound.minorLabel}`
  }

  return `${round(value, display.decimals)} ${display.label}`
}

// "4 ft (1.22 m)" — the chosen unit first, the canonical equivalent in
// brackets; collapses to "1.22 m" when the chosen unit IS canonical.
export const formatWithCanonical = (
  dimension: string,
  canonicalValue: number,
  unit: string
): string => {
  const dim = Dimensions[dimension]
  if (unit === dim.canonical) return formatIn(dimension, canonicalValue, unit)

  return `${formatIn(dimension, canonicalValue, unit)} (${formatIn(
    dimension,
    canonicalValue,
    dim.canonical
  )})`
}

// The generic-field rendering: a canonical value in its dimension's default
// display unit — what schema-declared `unit:` fields format through when no
// per-document preference applies.
export const formatField = (dimension: string, canonicalValue: number): string =>
  formatIn(dimension, canonicalValue, Dimensions[dimension].defaultDisplay)
